import { useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { wsUrl } from "../lib/api";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, PhoneIncoming } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";

const MAX_WS_ATTEMPTS = 5;

/**
 * Persistent authed WebSocket for residents/admins/guards.
 *
 * Behavior worth noting:
 *  - When a resident is already inside a call (`/call/*`), a second
 *    `incoming_call` must not hijack their session. We auto-reject the new
 *    call with `reason=busy` (over HTTP so the backend fires the activity +
 *    kiosk `call_status`), and don't navigate.
 *  - Otherwise the resident is navigated to /call/:id in "callee" mode.
 */
export function GlobalWs() {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const attemptsRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    if (!user || !user.access_token) return undefined;
    let cancelled = false;
    attemptsRef.current = 0;

    function connect() {
      if (cancelled) return;
      if (attemptsRef.current >= MAX_WS_ATTEMPTS) return;
      attemptsRef.current += 1;

      let ws;
      try {
        const url = wsUrl(`/api/ws/${user.id}?token=${encodeURIComponent(user.access_token)}`);
        ws = new WebSocket(url);
      } catch {
        return;
      }
      wsRef.current = ws;
      window.__fastviWs = ws;

      ws.onopen = () => { attemptsRef.current = 0; };
      ws.onmessage = (ev) => {
        let msg;
        try { msg = JSON.parse(ev.data); } catch { return; }

        if (msg.type === "incoming_call" && user.role === "resident") {
          const path = locationRef.current?.pathname || "";
          const onACall = path.startsWith("/call/");
          if (onACall) {
            // Busy: auto-reject over HTTP so the backend fires the activity
            // event and notifies the kiosk with `reason=busy`.
            axios.post(
              `${API}/calls/action`,
              { call_id: msg.call_id, action: "reject", reason: "busy" },
              { withCredentials: true }
            ).catch(() => { /* noop */ });
            return;
          }
          toast(`Incoming call from ${msg.visitor_name}`, {
            description: `Unit ${msg.unit_number || ""}`,
            icon: <PhoneIncoming size={16} />,
            action: {
              label: "Answer",
              onClick: () => navigate(`/call/${msg.call_id}?role=callee`),
            },
            duration: 30000,
          });
          navigate(`/call/${msg.call_id}?role=callee&visitor=${encodeURIComponent(msg.visitor_name)}`);
        }
        if (window.__fastviSignalHandler) window.__fastviSignalHandler(msg);
      };
      ws.onclose = () => {
        // Stale-WS guard so a socket we've already replaced can't retrigger
        // the reconnect loop (React StrictMode double-mount, etc.).
        if (wsRef.current !== ws) return;
        wsRef.current = null;
        window.__fastviWs = null;
        if (!cancelled && attemptsRef.current < MAX_WS_ATTEMPTS) {
          reconnectTimer.current = setTimeout(connect, 2000);
        }
      };
      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    }

    connect();
    return () => {
      cancelled = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } }
      window.__fastviWs = null;
    };
  }, [user, navigate]);

  if (!user) return null;
  return (
    <div
      className="pointer-events-none fixed bottom-3 right-3 z-10 hidden md:flex items-center gap-2 text-xs font-mono text-muted-foreground bg-card/80 border border-border rounded-full px-3 py-1.5 shadow-sm"
      data-testid="ws-indicator"
    >
      <Bell size={12} />
      <span data-testid="ws-status">live</span>
    </div>
  );
}
